/**
 * PACE REPORT
 */

import React, { useState, useEffect } from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { getHeaders } from "@/contexts/AuthContext";
import PaceReportBarChart from "./components/pacechart";
import { useToast } from "@/hooks/use-toast";
import { fetchProperties as fetchPropertiesApi } from "@/api/property";
import { get } from 'http';

const Report: React.FC = () => {  
  const [propertyData, setPropertyData] = useState<any[]>([]);
  const [startDisplayDate, setStartDisplayDate] = useState<Date | undefined>(undefined);
  const [endDisplayDate, setEndDisplayDate] = useState<Date | undefined>(undefined);
  const [propertyId, setPropertyId] = useState('');
  const [compareTo, setCompareTo] = useState('lastYear');
  const [groupBy, setGroupBy] = useState('none');
  const [paceData, setPaceData] = useState<any>('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    // Retrieve all properties from API
    const getProperties = async () => {
      try {
        setLoading(true);
        const data = await fetchPropertiesApi();
        if (Array.isArray(data)) {
          setPropertyData(data);
        } else {
          console.error("Fetched property data is not an array:", data);
          setPropertyData([]);
          toast({
            title: "Error",
            description: "Could not load properties data properly",
            variant: "destructive",
          });
        }
      } catch (error) {
        console.error("Error fetching properties:", error);
        setPropertyData([]);
        toast({
          title: "Error",
          description: "Failed to fetch properties",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    getProperties();
  }, [toast]);

  async function generateReport(): Promise<void> {
    // Check for valid values in all fields
    if (!propertyId || !startDisplayDate || !endDisplayDate) {
      setError("Please fill in all fields");
      return;
    } 
    
    setError("");
    setLoading(true);
    
    try {
      const headers = await getHeaders();
      const body = {
        "propertyId": propertyId,
        "compareTo": compareTo,
        "groupBy": groupBy,
        "dateFrom": startDisplayDate,
        "dateTo": endDisplayDate
      };
      
      // const response = getDummyData();
      const response = await fetch(`${import.meta.env.VITE_API_URL}/rms/pace`, {
        mode: 'cors',
        method: 'POST', 
        headers: headers,
        body: JSON.stringify(body)
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      // const data = getDummyData();
      console.log("Pace report data:", data);
      setPaceData(data);
      
      toast({
        title: "Report Generated",
        description: "Pace report has been successfully generated",
      });
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("Failed to generate report");
      toast({
        title: "Error",
        description: "Failed to generate report",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }

  const getDummyData = () => {
    return {
        "Title": "Pace Report",
        "report": [
            {
                "propertyId": 7,
                "propertyName": "Boulevard Motor Inn",
                "categoryName": "",
                "categoryId": 0,
                "categoryGroupType": "",
                "categoryGroupTypeId": 0,
                "current": {
                    "reservation": 693,
                    "nights": 1065,
                    "grossRevenue": 100.00,
                    "nettRevenue": 200.00,
                    "averageDailyRateGross": 17.24,
                    "averageDailyRateNett": 15.76,
                    "occupancyPercent": 74.63
                },
                "previous": {
                    "reservation": 636,
                    "nights": 1219,
                    "grossRevenue": 300.00,
                    "nettRevenue": 417.63,
                    "averageDailyRateGross": 38.98,
                    "averageDailyRateNett": 26.35,
                    "occupancyPercent": 82.93
                },
                "variance": {
                    "reservation": 57,
                    "reservationPercent": 8.96,
                    "nights": -154,
                    "nightsPercent": -12.63,
                    "grossRevenue": 882.79,
                    "grossRevenuePercent": 7.01,
                    "nettRevenue": 801.81,
                    "nettRevenuePercent": 7.01,
                    "averageDailyRateGross": 31.26,
                    "averageDailyRateGrossPercent": 22.49,
                    "averageDailyRateNett": 28.41,
                    "averageDailyRateNettPercent": 22.49,
                    "occupancyPercent": -8.3
                }
            }
        ]
    }
  }

  return (
    <div className="min-h-screen">
      <div className="p-4">
        <h1 className="text-2xl font-semibold mb-4">Pace Report</h1>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 p-4">
        {/* Filter Panel */}
        <div className="lg:col-span-3">
          <Card>
            <CardHeader>
              <CardTitle>Report Parameters</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="property">Property</Label>
                <Select value={propertyId} onValueChange={setPropertyId}>
                  <SelectTrigger id="property" disabled={loading || !propertyData.length}>
                    <SelectValue placeholder="Select a property" />
                  </SelectTrigger>
                  <SelectContent>
                    {propertyData && propertyData.length > 0 ? (
                      propertyData.map((item: any) => (
                        <SelectItem key={item.id} value={item.id.toString()}>
                          {item.name}
                        </SelectItem>
                      ))
                    ) : (
                      <SelectItem value="loading" disabled>
                        {loading ? "Loading properties..." : "No properties available"}
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="dateFrom">From</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      id="dateFrom"
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !startDisplayDate && "text-muted-foreground"
                      )}
                      disabled={loading}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {startDisplayDate ? format(startDisplayDate, "PPP") : <span>Pick a date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={startDisplayDate}
                      onSelect={setStartDisplayDate}
                      initialFocus
                      className="pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="dateTo">To</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      id="dateTo"
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !endDisplayDate && "text-muted-foreground"
                      )}
                      disabled={loading}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {endDisplayDate ? format(endDisplayDate, "PPP") : <span>Pick a date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={endDisplayDate}
                      onSelect={setEndDisplayDate}
                      initialFocus
                      className="pointer-events-auto"
                    />
                  </PopoverContent>
                </Popover>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="compareTo">Compare To</Label>
                <Select value={compareTo} onValueChange={setCompareTo} disabled={loading}>
                  <SelectTrigger id="compareTo">
                    <SelectValue placeholder="Select comparison" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="lastYear">Last Year</SelectItem>
                    <SelectItem value="yearBeforeLast">Year before last</SelectItem>
                    <SelectItem value="lastYearAndYearBeforeLast">Last Year & Year before last</SelectItem>
                    <SelectItem value="noComparison">None</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="groupBy">Group By</Label>
                <Select value={groupBy} onValueChange={setGroupBy} disabled={loading}>
                  <SelectTrigger id="groupBy">
                    <SelectValue placeholder="Select grouping" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="category">Room Category</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <Button 
                className="w-full mt-4" 
                onClick={() => generateReport()}
                disabled={loading || !propertyId || !startDisplayDate || !endDisplayDate}
              >
                {loading ? "Generating..." : "Generate Report"}
              </Button>
              
              {error && (
                <p className="text-sm font-medium text-destructive mt-2">{error}</p>
              )}
            </CardContent>
          </Card>
        </div>
        
        {/* Results Panel */}
        <div className="lg:col-span-9">
          <Card className="h-full">
            <CardContent className="p-6">
              {loading ? (
                <div className="h-full flex items-center justify-center">
                  <p className="text-muted-foreground text-lg">Loading data...</p>
                </div>
              ) : paceData ? (
                <div>
                  <h2 className="text-2xl font-bold mb-6">
                    {paceData.report && paceData.report[0] ? paceData.report[0].propertyName : "Report"}
                  </h2>
                  <PaceReportBarChart data={paceData} />
                </div>
              ) : (
                <div className="h-full flex items-center justify-center">
                  <p className="text-muted-foreground text-lg">
                    Select parameters and generate a report to view data
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Report;
